<template>
  <div class="br-map-panel">
    <div class="br-map-wrap">
      <svg
        ref="svg"
        class="br-map"
        :class="{ 'is-checking': checking }"
        :viewBox="viewBox"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          v-for="loc in locations"
          :key="loc.id"
          :ref="'path-' + loc.id"
          :d="loc.path"
          class="state"
          :class="stateCls(loc.id)"
          :fill="fillOf(loc.id)"
          @mouseenter="showTip(loc.id)"
          @mousemove="moveTip($event)"
          @mouseleave="hideTip"
          @click="handleClick(loc.id)"
        />
        <text
          v-for="l in labels"
          :key="'label-' + l.uf"
          class="state-label"
          :class="l.cls"
          :x="l.x"
          :y="l.y"
          :font-size="l.size"
          text-anchor="middle"
        >{{ l.uf }}</text>
      </svg>

      <!-- 右侧图例：5 类基础颜色 + 即将过期（橙色渐变） -->
      <aside class="map-legend">
        <h5>
          <span>图例 · 27 州</span>
          <span class="lg-pt">Legenda · 27 estados</span>
        </h5>
        <div v-for="item in legendItems" :key="item.key" class="lg-item">
          <span
            class="lg-dot"
            :class="{ 'lg-expire-dot': item.key === 'expire' }"
            :style="item.dotStyle"
          />
          <span class="lg-text">
            <span class="lg-zh">{{ item.zh }}</span>
            <span class="lg-pt">{{ item.pt }}</span>
          </span>
          <span class="lg-count" :class="{ 'lg-expire-count': item.key === 'expire' }">{{ item.count }}</span>
        </div>
      </aside>
    </div>

    <div class="map-footnote">
      <div>
        实际服务 <b>{{ states.length }}</b> 州 · 监控 <b>{{ states.length }}</b> 州
        <span class="fn-pt">
          Cobertura real: {{ states.length }} estados · monitorados: {{ states.length }} estados
        </span>
      </div>
      <div class="fn-note">
        （北部 {{ unmonitoredNum }} 州：{{ northText }} 业务未对外报价，灰色未覆盖）
        <span class="fn-pt">({{ unmonitoredNum }} estados do Norte: {{ northText }} sem tarifas publicadas; cinza = não coberto)</span>
      </div>
      <div class="fn-tip">
        点击州块查看双轨状态 + 预计到期
        <span class="fn-pt">Clique num estado para ver o status das duas trilhas + vencimento previsto</span>
      </div>
    </div>

    <!-- 跟随鼠标的地图 tooltip（中葡双语） -->
    <div
      class="map-tooltip"
      :class="{ show: tip.show }"
      :style="{ left: tip.x + 'px', top: tip.y + 'px' }"
    >
      <div class="tip-uf">
        {{ tip.uf }}<span v-if="tip.companyName" class="tip-co"> · {{ tip.companyName }}</span>
        <span v-if="tip.ufPt" class="tip-pt tip-uf-pt">/ {{ tip.ufPt }}</span>
      </div>
      <div v-for="row in tip.rows" :key="row.key" class="tip-row">
        <span class="tip-k">{{ row.k }}<span class="tip-pt"> / {{ row.kPt }}:</span></span>
        <b :style="{ color: row.color }">{{ row.v }}<span v-if="row.vPt" class="tip-pt"> / {{ row.vPt }}</span></b>
      </div>
      <div v-if="tip.reason" class="tip-reason">
        {{ tip.reason }}<span class="tip-pt"> / {{ tip.reasonPt }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import brazilMap from '@svg-maps/brazil';
import { TAX_LABEL, SYS_LABEL, daysLeft } from '../mock';

// 地图着色（浅色系 + 深灰描边），与图例一一对应
// key 与地图接口 legend 的字段名保持一致：ok / taxErr / sysErr / noRecord / none
const COLORS = {
  ok: '#c9ecd6', // 双轨正常 · 浅绿
  taxErr: '#f7c4c4', // IE 失效 · 浅红
  sysErr: '#fbd9b4', // 系统未配置 · 浅橙
  noRecord: '#f8eec0', // 税务未录入 · 浅黄
  none: '#eef1f4' // 未覆盖 · 浅灰
};

const OK_COLOR = '#7dffb0';
const ERR_COLOR = '#ff8a8a';
const WARN_COLOR = '#ffd285';

/** 异常原因：地图接口只给状态枚举，tooltip 的原因行按状态推导（中葡） */
const TAX_REASON = {
  2: { zh: 'IE 失效（被注销）', pt: 'IE Baixada (cancelada)' },
  3: { zh: '税务状态未知', pt: 'Status fiscal desconhecido' },
  4: { zh: '税务资质未录入', pt: 'Qualificação fiscal não registrada' }
};
const SYS_REASON = {
  2: { zh: '系统未配置开票主体', pt: 'Entidade emissora não configurada no sistema' }
};

export default {
  name: 'BrazilMap',
  props: {
    // 州状态（字段同地图接口 items：uf / subjectId / tax / sys / overall / validade / daysLeft / expiring / covered / enabled）
    states: { type: Array, default: () => [] },
    // IE 主体明细（提供公司名称 / CNPJ 等，字段同 IE 分页接口 records）
    ieTable: { type: Array, default: () => [] },
    // 图例统计（字段同地图接口 legend），未传时按 states 实时统计
    legend: { type: Object, default: null },
    // 全部检查执行中：州块半透明
    checking: { type: Boolean, default: false },
    // 到期提醒提前天数
    expireWarnDays: { type: Number, default: 30 },
    // 未监控的州数量
    unmonitoredNum: { type: Number, default: 0 },
    // 未监控的州列表
    unmonitoredUf: { type: Array, default: () => [] }
  },
  data() {
    return {
      viewBox: brazilMap.viewBox,
      locations: brazilMap.locations || [],
      labels: [],
      tip: {
        show: false,
        x: 0,
        y: 0,
        uf: '',
        ufPt: '',
        companyName: '',
        rows: [],
        reason: '',
        reasonPt: ''
      }
    };
  },
  computed: {
    /** 未纳入监控的北部州拼接文案（地图脚注里说明哪几州未覆盖） */
    northText() {
      return this.unmonitoredUf.map(item => item.uf).join('/');
    },
    /** UF → 州状态行的映射，便于给每个州块着色 / 取异常原因 */
    stateMap() {
      const m = {};
      // 键统一转大写，避免接口 uf 为小写时匹配不上（匹配不上会被当成未覆盖，涂灰）
      this.states.forEach(s => { if (s.uf) m[String(s.uf).toUpperCase()] = s; });
      return m;
    },
    /** UF → IE 明细行的映射，便于按州取有效期、公司名称等字段 */
    ieMap() {
      const m = {};
      this.ieTable.forEach(r => { m[r.uf] = r; });
      return m;
    },
    /** 图例计数：直接用接口返回的 legend（后端口径），接口未给 legend 时才按州状态本地统计 */
    counts() {
      if (this.legend) {
        return {
          ok: this.legend.ok || 0,
          taxErr: this.legend.taxErr || 0,
          sysErr: this.legend.sysErr || 0,
          noRecord: this.legend.noRecord || 0,
          none: this.legend.none || 0
        };
      }
      const c = { ok: 0, taxErr: 0, sysErr: 0, noRecord: 0, none: 0 };
      // 与着色同一套判定（statusKeyOf），保证没有 legend 时图例数字与地图颜色一致
      this.locations.forEach(loc => {
        const key = this.statusKeyOf(loc.id);
        if (c[key] !== undefined) c[key] += 1;
      });
      return c;
    },
    /** 即将过期：接口 legend.expiring 优先，否则按 daysLeft 落在预警窗口内的记录数 */
    expiringCount() {
      if (this.legend && typeof this.legend.expiring === 'number') return this.legend.expiring;
      return this.ieTable.filter(r => {
        const d = typeof r.daysLeft === 'number' ? r.daysLeft : daysLeft(r.validade);
        return d !== null && d >= 0 && d <= this.expireWarnDays;
      }).length;
    },
    /** 图例项：中葡文案 + 实时计数 + 圆点配色 */
    legendItems() {
      const c = this.counts;
      return [
        { key: 'ok', zh: '双轨正常', pt: 'Normal (Duas Trilhas)', count: c.ok, dotStyle: { background: COLORS.ok, borderColor: '#5cb87f' }},
        { key: 'taxErr', zh: 'IE 失效', pt: 'IE Baixada', count: c.taxErr, dotStyle: { background: COLORS.taxErr, borderColor: '#e08a8a' }},
        { key: 'sysErr', zh: '系统未配置', pt: 'Não Configurado no Sistema', count: c.sysErr, dotStyle: { background: COLORS.sysErr, borderColor: '#dfa05e' }},
        { key: 'noRecord', zh: '税务未录入', pt: 'Não Registrado (Fisco)', count: c.noRecord, dotStyle: { background: COLORS.noRecord, borderColor: '#d4b95c' }},
        { key: 'none', zh: '未覆盖(北部)', pt: 'Não Coberto (Norte)', count: c.none, dotStyle: { background: COLORS.none, borderColor: '#9aa5b0' }},
        { key: 'expire', zh: '即将过期', pt: 'Vencimento Próximo', count: this.expiringCount, dotStyle: {}}
      ];
    }
  },
  mounted() {
    this.$nextTick(() => this.computeLabels());
    // 兜底：容器初始不可见时 getBBox 取不到值，稍后重试一次
    this.labelTimer = setTimeout(() => this.computeLabels(), 300);
  },
  beforeDestroy() {
    clearTimeout(this.labelTimer);
  },
  methods: {
    /** SVG 的州 id 转大写 UF（@svg-maps/brazil 的 id 为小写州名缩写） */
    ufOf(id) {
      return String(id).toUpperCase();
    },
    /** 取该 UF 在看板 STATES 里的状态行；未覆盖的州返回 undefined */
    stateOf(id) {
      return this.stateMap[this.ufOf(id)];
    },
    /** 枚举值归一化：兼容后端返回数字 2 / 字符串 '2'，缺失或非数字返回 null */
    num(v) {
      if (v === null || v === undefined || v === '') return null;
      const n = Number(v);
      return Number.isNaN(n) ? null : n;
    },

    /**
     * 着色判断顺序（自上面下命中即返回）：
     *   1 未覆盖：enabled=false（未纳入监控）或该州不在 items 里 → 不看 tax / sys
     *   2 双轨正常：tax=1 且 sys=1
     *   3 IE 失效：tax=2，或 tax=3 且 sys=1
     *   4 系统未配置：sys=2
     *   5 税务未录入：tax=4，或 enabled=true 且 tax 与 sys 都为 null
     *   6 其余：未覆盖
     * 枚举统一转数字，兼容后端返回 '1' / '2' 这类字符串
     */
    statusKeyOf(id) {
      const st = this.stateOf(id);
      if (!st) return 'none';
      // enabled=false / 0：未纳入监控，优先级最高
      if (this.num(st.enabled) === 0) return 'none';
      const tax = this.num(st.tax);
      const sys = this.num(st.sys);
      if (tax === 1 && sys === 1) return 'ok';
      if (tax === 2 || (tax === 3 && sys === 1)) return 'taxErr';
      if (sys === 2) return 'sysErr';
      if (tax === 4 || (tax === null && sys === null)) return 'noRecord';
      return 'none';
    },
    /** 按状态返回州块填充色 */
    fillOf(id) {
      return COLORS[this.statusKeyOf(id)];
    },
    /** 按状态返回州块样式类；未覆盖用 is-disabled（状态 key 转成 kebab：noRecord → is-no-record） */
    stateCls(id) {
      const key = this.statusKeyOf(id);
      if (key === 'none') return 'is-disabled';
      return `is-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
    },
    /** 点击州块：把该州 UF 抛给父组件（未覆盖州不响应） */
    handleClick(id) {
      const st = this.stateOf(id);
      if (!st) return; // 北部 7 州未纳入监控，不可点击
      this.$emit('select', this.ufOf(id));
    },
    /** 悬浮州块：组装 tooltip 数据（税务 / 系统 / 预计到期 三行 + 异常原因）；未覆盖州显示占位行 */
    showTip(id) {
      const uf = this.ufOf(id);
      const st = this.stateOf(id);
      const ie = this.ieMap[uf];
      if (!st) {
        this.tip = {
          show: true,
          x: this.tip.x,
          y: this.tip.y,
          uf,
          ufPt: 'Não Coberto',
          companyName: '',
          rows: [
            { key: 'tax', k: '税务', kPt: 'Status Fiscal', v: '—', color: '#c9cdd4' },
            { key: 'sys', k: '系统', kPt: 'Config. Sistema', v: '—', color: '#c9cdd4' },
            { key: 'expire', k: '预计到期', kPt: 'Vencimento Previsto', v: '—', color: '#c9cdd4' }
          ],
          reason: '未纳入监控范围（北部）',
          reasonPt: 'Fora do escopo de monitoramento (Norte)'
        };
        return;
      }
      const taxNum = this.num(st.tax);
      const sysNum = this.num(st.sys);
      const tax = TAX_LABEL[taxNum] || { zh: '—', pt: '—' };
      const sys = SYS_LABEL[sysNum] || { zh: '—', pt: '—' };
      // 剩余天数：优先取接口的 daysLeft（接口可能给 null），缺失时按 validade 计算
      const days = typeof st.daysLeft === 'number' ? st.daysLeft : daysLeft(st.validade);
      let expireText = '—';
      let expireTextPt = '—';
      let expireColor = '#fff';
      if (days !== null) {
        if (days < 0) {
          expireText = `已过期 ${Math.abs(days)} 天`;
          expireTextPt = `vencido há ${Math.abs(days)} dias`;
          expireColor = ERR_COLOR;
        } else if (days <= this.expireWarnDays) {
          expireText = `剩 ${days} 天 · 预警`;
          expireTextPt = `${days} dias restantes · Alerta`;
          expireColor = WARN_COLOR;
        } else {
          expireText = `剩 ${days} 天`;
          expireTextPt = `${days} dias restantes`;
          expireColor = OK_COLOR;
        }
        expireText = `${st.validade} · ${expireText}`;
        expireTextPt = `${st.validade} · ${expireTextPt}`;
      } else {
        expireText = '—（未录入）';
        expireTextPt = '— (Não Registrado)';
        expireColor = '#c9cdd4';
      }
      this.tip = {
        show: true,
        x: this.tip.x,
        y: this.tip.y,
        uf,
        ufPt: '',
        // 公司名称来自 IE 明细（地图接口 items 不含公司名）
        companyName: (ie && ie.companyName) || '',
        rows: [
          {
            key: 'tax',
            k: '税务',
            kPt: 'Status Fiscal',
            v: tax.zh,
            vPt: tax.pt,
            color: taxNum === 1 ? OK_COLOR : (taxNum === 2 ? ERR_COLOR : WARN_COLOR)
          },
          {
            key: 'sys',
            k: '系统',
            kPt: 'Config. Sistema',
            v: sys.zh,
            vPt: sys.pt,
            color: sysNum === 1 ? OK_COLOR : ERR_COLOR
          },
          {
            key: 'expire',
            k: '预计到期',
            kPt: 'Vencimento Previsto',
            v: expireText,
            vPt: expireTextPt,
            color: expireColor
          }
        ],
        reason: (TAX_REASON[taxNum] || SYS_REASON[sysNum] || {}).zh || '',
        reasonPt: (TAX_REASON[taxNum] || SYS_REASON[sysNum] || {}).pt || ''
      };
    },
    /** 跟随鼠标移动 tooltip；贴近右边缘时自动左翻，避免超出屏幕 */
    moveTip(e) {
      const tipW = 440;
      const x = e.clientX + 14 + tipW > window.innerWidth ? Math.max(e.clientX - tipW - 14, 8) : e.clientX + 14;
      this.tip.x = x;
      this.tip.y = e.clientY + 14;
    },
    /** 移出州块时隐藏 tooltip */
    hideTip() {
      this.tip.show = false;
    },
    /** 按州块面积自适应字号（下限 12px），保证 DF 等小州也能标出州名缩写 */
    computeLabels() {
      if (!this.locations.length) return;
      const list = [];
      this.locations.forEach(loc => {
        const refs = this.$refs['path-' + loc.id];
        const el = Array.isArray(refs) ? refs[0] : refs;
        if (!el || typeof el.getBBox !== 'function') return;
        let bbox = null;
        try { bbox = el.getBBox(); } catch (e) { bbox = null; }
        if (!bbox || !bbox.width || !bbox.height) return;
        const w = bbox.width;
        const h = bbox.height;
        // 字号下限 12px（模块规范：不小于 12px）
        let size = 16;
        let cls = '';
        if (w < 32 || h < 22) { size = 12; cls = 'is-tiny'; } else if (w < 60 || h < 40) { size = 13; cls = 'is-small'; }
        list.push({
          uf: this.ufOf(loc.id),
          x: bbox.x + w / 2,
          y: bbox.y + h / 2 + size * 0.35,
          size,
          cls
        });
      });
      if (list.length) this.labels = list;
    }
  }
};
</script>

<style scoped lang="scss">
.br-map-panel {
  width: 100%;
}
.br-map-wrap {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}
.br-map {
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
  height: auto;
  max-height: 600px;
  display: block;
  filter: drop-shadow(0 2px 10px rgba(61, 74, 87, 0.12));
}
.br-map .state {
  cursor: pointer;
  stroke: #2d3742;
  stroke-width: 1.2;
  stroke-linejoin: round;
  stroke-linecap: round;
  transition: fill 0.15s, stroke-width 0.15s;
}
.br-map .state:hover {
  stroke-width: 2.4;
  filter: brightness(0.92);
}
.br-map .state.is-disabled {
  cursor: default;
}
.br-map .state.is-disabled:hover {
  stroke-width: 1.2;
  filter: none;
}
.br-map.is-checking .state {
  filter: grayscale(0.35) brightness(1.1);
}
.br-map .state-label {
  pointer-events: none;
  user-select: none;
  font-weight: 700;
  fill: #2d3742;
  paint-order: stroke;
  stroke: #fff;
  stroke-width: 3px;
  stroke-linejoin: round;
}
.br-map .state-label.is-small {
  stroke-width: 2.5px;
}
.br-map .state-label.is-tiny {
  stroke-width: 2px;
}

/* 图例 */
.map-legend {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 200px;
  flex-shrink: 0;
  padding-left: 12px;
  border-left: 1px dashed #f0f1f5;
}
.map-legend h5 {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  color: #86909c;
  font-weight: 600;
  margin-bottom: 6px;
}
.map-legend h5 .lg-pt {
  font-size: 12px;
  font-weight: 400;
  color: #86909c;
}
.map-legend .lg-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #4e5969;
}
.map-legend .lg-dot {
  width: 14px;
  height: 14px;
  border-radius: 4px;
  border: 1.5px solid #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}
.map-legend .lg-dot.lg-expire-dot {
  background: linear-gradient(135deg, #ff7d00 0%, #ffb84d 100%);
  border-color: #fff;
}
.map-legend .lg-text {
  display: flex;
  flex-direction: column;
  line-height: 1.35;
  min-width: 0;
}
.map-legend .lg-text .lg-pt {
  font-size: 12px;
  color: #86909c;
}
.map-legend .lg-count {
  margin-left: auto;
  font-weight: 600;
  color: #1d2129;
}
.map-legend .lg-count.lg-expire-count {
  color: #ff7d00;
  font-weight: 700;
}

/* 脚注 */
.map-footnote {
  margin: 100px 0;
  padding: 8px 10px;
  font-size: 12px;
  line-height: 1.7;
  color: #4e5969;
  background: #fafbfc;
  border-left: 3px solid #00a551;
  border-radius: 0 4px 4px 0;
}
.map-footnote b {
  color: #1d2129;
}
.map-footnote .fn-pt {
  display: block;
  font-size: 12px;
  color: #86909c;
}
.map-footnote .fn-tip {
  color: #1d2129;
}

/* tooltip */
.map-tooltip {
  position: fixed;
  pointer-events: none;
  z-index: 3000;
  background: #1d2129;
  color: #fff;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.7;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
  display: none;
  max-width: 440px;
}
.map-tooltip.show {
  display: block;
}
.map-tooltip .tip-uf {
  font-weight: 700;
  font-size: 13px;
  color: #00a551;
  margin-bottom: 2px;
}
.map-tooltip .tip-uf-pt {
  color: #c9cdd4;
  font-weight: 400;
}
.map-tooltip .tip-co {
  color: #fff;
  font-weight: 500;
}
.map-tooltip .tip-row {
  display: flex;
  gap: 8px;
  align-items: baseline;
  flex-wrap: wrap;
}
.map-tooltip .tip-k {
  color: #c9cdd4;
  flex-shrink: 0;
}
.map-tooltip .tip-pt {
  color: #c9cdd4;
  font-size: 12px;
  font-weight: 400;
}
.map-tooltip .tip-reason {
  margin-top: 4px;
  color: #c9cdd4;
}
</style>
